import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from "react-redux";
import api from 'state/axios';
import { format } from "date-fns-tz";
import toast from 'react-hot-toast';
import { useTheme, useMediaQuery, Box, Typography,
  CircularProgress, IconButton, Tooltip, 
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { useDateRangeContext } from 'contexts/DateRangeContext';
import useAuthCheck from 'hooks/useAuthCheck';
import { LAYOUT_HEIGHT } from 'state/constants';
import ScrollToTop from 'components/ScrollToTop';
import Header from 'components/Header';
import FlexBetween from 'components/FlexBetween';
import FlexCenter from 'components/FlexCenter';
import DataGridComponent from 'components/DataGridComponent';
import RouteCrewListDialog from 'components/RouteCrewListDialog';
import UnfinalizedRouteList from "./UnfinalizedRouteList";


const UnfinalizedCrewRoutes = () => {
  useAuthCheck();
  const dispatch = useDispatch();
  const timeMode = useSelector((state) => state.timemode);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isDesktop = useMediaQuery(theme.breakpoints.up('lg'));

  const { startDate, endDate } = useDateRangeContext();
  
  const scrollRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  const [openRouteCrewListDialog, setOpenRouteCrewListDialog] = useState(false);
  
  const [crewList, setCrewList] = useState([]);
  const [crewRoute, setCrewRoute] = useState([]);
  const [unfinalizedCrewRoutes, setUnfinalizedCrewRoutes] = useState([]);
    
  
  useEffect(() => {  
    const handleUnfinalizedCrewRoutes = async () => {
      if (!startDate || !endDate) return null; 
      
      try {
        setError(null);
        setLoading(true);
        
        const url = '/unfinalizedcrewroutes';
        const payload = { startDate, endDate };          
        
        const res = await api.post(url, payload);

        setUnfinalizedCrewRoutes(res.data);
      } catch (error) {
        setError(error)
        toast.error(error?.message || "Failed to fetch unfinalized crew routes!");
      } finally {
        setLoading(false);
      }
    };
    
    handleUnfinalizedCrewRoutes();
  }, [timeMode, startDate, endDate, dispatch]); 
      
  const handleRouteCrewList = useCallback((param) => {
    const routeDate = new Date(param.routeDate);
    const routeNo = param.routeNo;

    const fetchLegMembersByRoute = async () => {
      try {
        const url = '/legmembers/legmembersbyrouteno';
        const payload = { routeDate, routeNo };      
        
        const res = await api.post(url, payload);

        if (!res.data) {
          toast.error("No data found for this route!");
          return;
        }
        
        const uniqueCrewList = Object.values(
          res.data.reduce((acc, item) => {
            if (!item.crewCode) return acc;

            // Only keep the first entry per crewCode
            if (!acc[item.crewCode]) {
              acc[item.crewCode] = {
                crewCode: item.crewCode,
                crewName: item.crewName,
                post: item.post,
                base: item.base,
                gender: item.gender,
                contactNo: item.contactNo,
                rankPriority: item.rankPriority,
              };
            }

            return acc;
          }, {})
        ).sort((a, b) => a.rankPriority - b.rankPriority);
        
        const routeMap = res.data.reduce((acc, item) => {
          if (!item.routeNo) return acc;

          const routeKey = `${item.routeDate}|${item.routeNo}`;
          if (!acc[routeKey]) {
            acc[routeKey] = {
              routeDate: item.routeDate,
              routeNo: item.routeNo,
              routeDetails: [],
              _flightKeys: new Set()
            };
          }

          const flightKey = `${item.flightDate}|${item.flightDesc}|${item.dep}`;
          if (!acc[routeKey]._flightKeys.has(flightKey)) {
            acc[routeKey].routeDetails.push({
              flightDate: item.flightDate,
              flightDesc: item.flightDesc,
              dep: item.dep,
              arr: item.arr,
              flightNo: item.flightNo,
              acType: item.acType,
              flightReg: item.flightReg,
              etd: item.etd,
              eta: item.eta,
              etdLocalBase: item.etdLocalBase,
              etaLocalBase: item.etaLocalBase,
              atd: item.atd,
              ata: item.ata,
              atdLocalBase: item.atdLocalBase,
              ataLocalBase: item.ataLocalBase,
              fullEtd: item.fullEtd,
              fullEta: item.fullEta,
              fullEtdLocalBase: item.fullEtdLocalBase,
              fullEtaLocalBase: item.fullEtaLocalBase,
              fullAtd: item.fullAtd,
              fullAta: item.fullAta,
              fullAtdLocalBase: item.fullAtdLocalBase,
              fullAtaLocalBase: item.fullAtaLocalBase,
              blockTime: item.blockTime,
            });
            acc[routeKey]._flightKeys.add(flightKey);
          }

          return acc;
        }, {});

        const uniqueFlightList = Object.values(routeMap).map(route => {
          delete route._flightKeys;

          route.routeDetails.sort((a, b) => new Date(a.fullEtd) - new Date(b.fullEtd));

          const first = route.routeDetails[0];
          const last  = route.routeDetails[route.routeDetails.length - 1];

          route.fullEtd = first?.fullEtd || null;
          route.fullEtdLocalBase = first?.fullEtdLocalBase || null;
          route.fullAtd = first?.fullAtd || null;
          route.fullAtdLocalBase = first?.fullAtdLocalBase || null;

          route.fullEta = last?.fullEta || null;
          route.fullEtaLocalBase = last?.fullEtaLocalBase || null;
          route.fullAta = last?.fullAta || null;
          route.fullAtaLocalBase = last?.fullAtaLocalBase || null;

          return route;
        });

        setCrewRoute(uniqueFlightList[0]);
        setCrewList(uniqueCrewList);        
        setOpenRouteCrewListDialog(true);
      } catch (error) {        
        toast.error(error.message || "Failed to fetch crew routes!");
      } 
    };

    fetchLegMembersByRoute();    
  }, []);

  const handleCloseRouteCrewListDialog = () => {
    setOpenRouteCrewListDialog(false);
    setCrewList([]);
  };
  
  const columns = useMemo(() => [
    {
      field: "no",
      headerName: "No",
      flex: 1,
      filterable: false,      
      sortable: false,
      renderCell: (params) => params.api.getAllRowIds().indexOf(params.id) + 1,
    },    
    {
      field: "routeDate",
      headerName: "RouteDate",
      flex: 2,
      valueFormatter: (params) => {
        return format(new Date(params), "dd/MM/yy");
      },
    },
    {
      field: "routeNo",
      headerName: "RouteNo",
      flex: 1.5,
    }, 
    {
      field: 'crewMembers',
      headerName: 'CrewMembers',
      flex: 6,    
      renderCell: (params) => {
        const sortedCrewMembers = [...params.row.crewMembers].sort((a, b) => a.rankPriority - b.rankPriority);
  
        return (
          <Box
            sx={{
              height: '100%',
              alignItems: 'flex-start',
              overflowY: 'auto',
              overflowX: 'hidden',
            }}
          >
            {sortedCrewMembers?.map((member, index) => (
              <Box
                key={index}
                display="flex"
                alignItems="center"
                alignContent='flex-start'
                gap={1}
                sx={{
                  color: member?.gender === 'FM' ? theme.palette.secondary.main : theme.palette.primary.dark,
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    textAlign: 'right',
                    whiteSpace: 'nowrap',
                    width: '30px',
                  }}
                >
                  {member.crewCode}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    textAlign: 'left',
                  }}
                >
                  {member.crewName}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    textAlign: 'center',
                  }}
                >
                  ({member.post} | {member.base}{member.trnDuty && ` | ${member.trnDuty}`})
                </Typography>                
              </Box>
            ))}
          </Box>
        );
      },
    }, 
  ], [theme]);

  const actionColumn = useMemo(() => [{
    field: "action",
    headerName: "Action",
    filterable: false,      
    sortable: false,
    width: 80,       
    renderCell: (params) => (
      <Box 
        height="100%" 
        display="flex" 
        justifyContent="center" 
        alignItems="center"
        gap={1}
      >
        <Tooltip title="View">
          <IconButton
            aria-label="View"
            sx={{
              color: theme.palette.primary.dark,
              cursor: 'pointer',
              '&:hover': {
                color: theme.palette.green.deep,
              }
            }}
            onClick={() => {              
              handleRouteCrewList(params.row)}
            }
          >
            <VisibilityIcon />
          </IconButton>
        </Tooltip>          
      </Box>
    ),
  }], [theme, handleRouteCrewList]); 

  const hiddenColumns = useMemo(() => {
    return {
      _id: false,
      no: false,
    };
  }, []); 
  
  const nonMobileHiddenColumns = useMemo(() => { 
    return {
      _id: false,
    }
  }, []);

  const columnsToShow = [...columns, ...actionColumn];
  const [columnVisibilityModel, setColumnVisibilityModel] = useState(
    isMobile ? hiddenColumns : nonMobileHiddenColumns
  );

  useEffect(() => {
    setColumnVisibilityModel(isMobile ? hiddenColumns : nonMobileHiddenColumns);
  }, [isMobile, hiddenColumns, nonMobileHiddenColumns]);


  return (
    <Box className="main-container-box">
      <FlexBetween>
        <Header 
          title="Unfinalized Crew Routes" 
          subtitle={`${loading ? 'Loading...' : 'List of Routes'}`}
        />                
      </FlexBetween>

      {loading ? (
        <FlexCenter height={LAYOUT_HEIGHT}>
          <CircularProgress />
        </FlexCenter>
      ) : error ? (
        <FlexCenter height={LAYOUT_HEIGHT}>
          <Typography color="error">Error: {error?.message || ''}</Typography>
        </FlexCenter>
      ) : (
        <Box
          display="grid"
          gap={2}
          gridTemplateColumns="repeat(12, minmax(0, 1fr))"
          sx={{
            "& > div": { gridColumn: isDesktop ? undefined : "span 12" },
          }}
        >
          <Box ref={scrollRef} height={LAYOUT_HEIGHT} sx={{ gridColumn: "span 12", overflowY: 'auto' }}>
            {isMobile ? (
              <UnfinalizedRouteList
                routes={unfinalizedCrewRoutes}
                onView={handleRouteCrewList}
              />
            ) : (
              <DataGridComponent                
                rows={unfinalizedCrewRoutes}
                columns={columnsToShow}
                columnVisibilityModel={columnVisibilityModel}
                setColumnVisibilityModel={setColumnVisibilityModel}
                loading={loading}
              />
            )}       
            
            <ScrollToTop scrollRef={scrollRef} />     
          </Box>          
        </Box>
      )}

      <ScrollToTop />

      {openRouteCrewListDialog &&
        <RouteCrewListDialog 
          open={openRouteCrewListDialog}
          onClose={handleCloseRouteCrewListDialog}
          crewRoute={crewRoute}
          crewList={crewList}
        />
      }
    </Box>
  );
};

export default UnfinalizedCrewRoutes;
